import { Injectable } from "@angular/core";
import { Signature, SignaturePlace } from "src/app/api/models";

export interface SignatureSetup {
  hasSignaturePlace: boolean;
  specificSignaturePlace: boolean;
  shortcut: string; // shortcut for the signature place
  title: string; // complete title, can be used as a tooltip or for display
  label: string; // what is really shown/written on the item signature
}

@Injectable({
  providedIn: "root",
})
export class SignatureService {
  constructor() {}

  /**
   * Constructs a SignatureSetup object based on the provided signature and places.
   *
   * Determines whether the signature has a designated signature place,
   * builds the title and shortcut by retrieving labels from the places,
   * and finally sets an appropriate label based on the signature data.
   *
   * @param signature - The signature object containing various signature properties.
   * @param places - The array of SignaturePlace objects used to retrieve label details.
   * @returns A SignatureSetup object with the computed properties.
   */
  getSignatureSetup(
    signature: Signature,
    places: SignaturePlace[]
  ): SignatureSetup {
    // Initialize the setup object with default values.
    const setup: SignatureSetup = {
      hasSignaturePlace: false,
      specificSignaturePlace: false,
      shortcut: "",
      title: "",
      label: "",
    };

    // Check if the signature indicates a designated signature place.
    // For example, signatureTypeId === 1 means a specific signature place is defined.
    setup.hasSignaturePlace = signature.signatureTypeId === 1;

    // Determine if any specific signature place is provided (horizontal, vertical, frontback, or other).
    // This flag helps differentiate between a generic signature place and one with specific positions.
    setup.specificSignaturePlace =
      signature.horizontal != null ||
      signature.vertical != null ||
      signature.frontback != null ||
      signature.other != null;

    // Build an array of title parts using the available signature position properties.
    // For each present signature property, retrieve its corresponding title label.
    const titleParts: string[] = [];
    if (signature.horizontal != null) {
      titleParts.push(
        this.getSignaturePlaceLabel(signature.horizontal, "title", places)
      );
    }
    if (signature.vertical != null) {
      titleParts.push(
        this.getSignaturePlaceLabel(signature.vertical, "title", places)
      );
    }
    if (signature.frontback != null) {
      titleParts.push(
        this.getSignaturePlaceLabel(signature.frontback, "title", places)
      );
    }
    if (signature.other != null) {
      titleParts.push(
        this.getSignaturePlaceLabel(signature.other, "title", places)
      );
    }
    // Combine the individual title parts with a space, forming the complete title.
    setup.title = titleParts.join(" ");

    // Build the shortcut string if a designated signature place exists.
    // This concatenates the "shortcut" labels for each available signature position.
    setup.shortcut = setup.hasSignaturePlace
      ? `${this.getSignaturePlaceLabel(
          signature.horizontal,
          "shortcut",
          places
        )}${this.getSignaturePlaceLabel(
          signature.vertical,
          "shortcut",
          places
        )}${this.getSignaturePlaceLabel(
          signature.frontback,
          "shortcut",
          places
        )}${this.getSignaturePlaceLabel(signature.other, "shortcut", places)}`
      : "";

    // Determine the label for the signature based on the provided realText and signature type.
    // Priority is given to a provided realText; if not available, a default is assigned based on placement and type.
    if (signature.realText) {
      // Use the real text if provided.
      setup.label = signature.realText;
    } else if (
      // If the signature has a designated place but no specific position and no realText,
      // assign a default label indicating a generic signature.
      setup.hasSignaturePlace &&
      !setup.specificSignaturePlace &&
      !signature.realText
    ) {
      setup.label = "Signováno";
    } else if (signature.signatureTypeId === 2) {
      // If signatureTypeId is 2, the work is not signed.
      setup.label = "Dílo není signováno";
    } else if (signature.signatureTypeId === 3) {
      // If signatureTypeId is 3, the signature is unknown.
      setup.label = "Signatura není známa";
    }

    // Return the fully constructed SignatureSetup object.
    return setup;
  }

  private getSignaturePlaceLabel(
    id: number,
    type: "title" | "shortcut",
    places: SignaturePlace[]
  ): string {
    const signature = places.find((a) => a.id === id);
    if (!signature) return "";
    switch (type) {
      case "title":
        return signature.titleCS;
      case "shortcut":
        return signature.shortcutCS;
      default:
        break;
    }
  }
}
